import { useForm } from 'react-hook-form';
import { useState } from 'react';
import ErrorBanner from './ErrorBanner';
import { counties } from '../constants/counties';

function Field({ label, error, required, hint, children }) {
  return (
    <div className="mb-3">
      <label className="form-label fw-semibold mb-1">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {hint && !error && <div className="form-text">{hint}</div>}
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

function Input({ register, placeholder, type = 'text', error }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      {...register}
      className={`form-control${error ? ' is-invalid' : ''}`}
    />
  );
}

function SectionHeading({ number, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="d-flex align-items-baseline gap-2">
        <span className="section-number">{number}</span>
        <h6 className="fw-bold mb-0">{title}</h6>
      </div>
      {subtitle && (
        <div className="form-text section-subtitle-primary mt-1">{subtitle}</div>
      )}
    </div>
  );
}

export default function NominationForm({ categories, onSuccess }) {
  const [submitting, setSubmitting]   = useState(false);
  const [apiError, setApiError]       = useState('');
  const [bioLength, setBioLength]     = useState(0);
  const [selectedCat, setSelectedCat] = useState(null);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm();

  const onCategoryChange = (id) => {
    setValue('category', id, { shouldValidate: true });
    setSelectedCat(categories.find((c) => String(c.id) === String(id)) || null);
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    setApiError('');
    const { submitNomination } = await import('../api/nominations');
    try {
      await submitNomination({ ...data, category: Number(data.category) });
      onSuccess(data.stagename);
    } catch (err) {
      const status = err.response?.status;
      const msg    = err.response?.data?.message;
      if (status === 422 && typeof msg === 'object') {
        Object.entries(msg).forEach(([field, messages]) =>
          setError(field, { message: Array.isArray(messages) ? messages[0] : messages })
        );
      } else if (status === 400) {
        setApiError(typeof msg === 'string' ? msg : 'Nominations are currently not open.');
      } else if (status === 401) {
        setApiError('This portal is not configured correctly. Please contact the organiser.');
      } else if (!err.response) {
        setApiError('Unable to connect. Please check your internet connection and try again.');
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="nomination-wrapper">
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4 p-sm-5">

          <p className="form-eyebrow">Submit a Nomination</p>
          <h2 className="form-main-title">Nominate your favourite</h2>

          <div className="nominee-notice">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-1">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>All details below are about the person/brand you are nominating.</span>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>

            {/* Section 1 — Personal */}


            <Field label="Brand / Company / Alias Name (The name to appear)" required error={errors.stagename?.message}>
              <Input register={register('stagename', { required: 'Brand name is required' })} placeholder="As they're known publicly" error={errors.stagename?.message} />
            </Field>
            
            <Field label="Category" required error={errors.category?.message} hint={selectedCat?.description || null}>
              <select
                {...register('category', { required: 'Please select a category' })}
                onChange={(e) => onCategoryChange(e.target.value)}
                className={`form-select${errors.category ? ' is-invalid' : ''}`}
              >
                <option value="">Select a category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {errors.category && <div className="invalid-feedback d-block">{errors.category.message}</div>}
            </Field>
 <SectionHeading number="01" title="Nominee's Official Names" />
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <Field label="First Name" error={errors.firstname?.message}>
                  <Input register={register('firstname')} placeholder="Jane" error={errors.firstname?.message} />
                </Field>
              </div>
              <div className="col-12 col-sm-6">
                <Field label="Last Name" error={errors.lastname?.message}>
                  <Input register={register('lastname')} placeholder="Naisula" error={errors.lastname?.message} />
                </Field>
              </div>
            </div>

            <Field label="Gender" error={errors.gender?.message}>
              <select
                {...register('gender')}
                className={`form-select${errors.gender ? ' is-invalid' : ''}`}
              >
                <option value="">Select gender...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </Field>

            <Field label="County" error={errors.county?.message}>
              <select
                {...register('county')}
                className={`form-select${errors.county ? ' is-invalid' : ''}`}
              >
                <option value="">Select county...</option>
                {counties.map((county) => (
                  <option key={county} value={county}>{county}</option>
                ))}
              </select>
            </Field>


            <hr className="my-4" />

            {/* Section 2 — Contact */}
            <SectionHeading number="02" title="Nominee's contact details" />
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <Field label="Nominee's Phone Number" error={errors.phone?.message}>
                  <Input register={register('phone', { minLength: { value: 9, message: 'Enter a valid phone number' } })} placeholder="e.g. 0722 000 000" type="tel" error={errors.phone?.message} />
                </Field>
              </div>
              <div className="col-12 col-sm-6">
                <Field label="Nominee's Alternative Phone" error={errors.altphone?.message}>
                  <Input register={register('altphone', { minLength: { value: 9, message: 'Enter a valid phone number' } })} placeholder="e.g. 0733 000 000" type="tel" error={errors.altphone?.message} />
                </Field>
              </div>
            </div>

            <Field label="Email Address" error={errors.email?.message}>
              <Input register={register('email', { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} placeholder="name@example.com" type="email" error={errors.email?.message} />
            </Field>

            <hr className="my-4" />

            {/* Section 3 — About */}
            <SectionHeading number="03" title="About the Nominee" subtitle="All fields in this section are optional" />

            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <Field label="YouTube Link" error={errors.youtube?.message}>
                  <Input register={register('youtube', { validate: (v) => !v || v.startsWith('https://') || 'Must start with https://' })} placeholder="https://youtube.com/watch?v=..." type="url" error={errors.youtube?.message} />
                </Field>
              </div>
              <div className="col-12 col-sm-6">
                <Field label="Instagram" error={errors.instagram?.message}>
                  <Input register={register('instagram')} placeholder="@handle" error={errors.instagram?.message} />
                </Field>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <Field label="Facebook" error={errors.facebook?.message}>
                  <Input register={register('facebook')} placeholder="facebook.com/page" error={errors.facebook?.message} />
                </Field>
              </div>
              <div className="col-12 col-sm-6">
                <Field label="TikTok" error={errors.tiktok?.message}>
                  <Input register={register('tiktok')} placeholder="@handle" error={errors.tiktok?.message} />
                </Field>
              </div>
            </div>

            <Field label="Brief Bio/About" error={errors.bio?.message}>
              <div className="position-relative">
                <textarea
                  {...register('bio', { maxLength: { value: 500, message: 'Bio must be 500 characters or less' } })}
                  placeholder="Tell us about the nominee..."
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setBioLength(e.target.value.length)}
                  className={`form-control nomination-textarea${errors.bio ? ' is-invalid' : ''}`}
                />
                <span className="position-absolute bottom-0 end-0 pb-2 pe-3 text-muted small">
                  {bioLength}/500
                </span>
              </div>
              {errors.bio && <div className="invalid-feedback d-block">{errors.bio.message}</div>}
            </Field>

            <ErrorBanner message={apiError} />

            <button
              type="submit"
              disabled={submitting}
              className="btn btn-submit w-100 text-white d-flex align-items-center justify-content-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Submitting...
                </>
              ) : (
                <>Submit Nomination &rarr;</>
              )}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}
